/**
 * Действия в диалоге: отправка, повтор, редактирование, удаление, реакции,
 * квитанции, mute/архив.
 *
 * CC §5.4 «Алгоритм отправки»: сообщение СНАЧАЛА появляется в истории как
 * ожидающее, и только потом уходит в сеть. `client_message_id` создаётся
 * один раз и не меняется при повторах (§5.2) — на нём держится идемпотентность.
 */
import { useCallback, useMemo, useRef } from 'react'

import { api, unwrap, uuid } from '@shared/api/client'
import type { Message } from '@shared/api/types'
import {
  conversationsRepo,
  draftsRepo,
  messagesRepo,
  queueRepo,
  type LocalMediaDraft,
  type PendingOp,
} from '@shared/db'
import { runQueue } from '@shared/sync/queue-runner'
import { syncEngine } from '@shared/sync/instance'

interface SendInput {
  conversationId: string
  senderId: string
  text: string
  replyTo?: Message['reply_to']
  media?: LocalMediaDraft | null
  /** Пересылка: id исходного сообщения; тип и текст копируются для локального показа. */
  forwardOf?: string
  forwardType?: 'text' | 'image' | 'video' | 'voice'
}

function baseOp(kind: PendingOp['kind']): Omit<PendingOp, 'kind'> & { kind: PendingOp['kind'] } {
  return {
    op_id: uuid(),
    kind,
    created_at: new Date().toISOString(),
    attempt_count: 0,
    next_attempt_at: null,
    last_error: null,
    claimed_by: null,
    claimed_at: null,
    idempotency_key: null,
  } as never
}

export function useChatActions() {
  // Значение false у loadOlder означает «ничего не вставлено»: лента использует
  // это для компенсации прокрутки. Последняя непустая страница тоже требует компенсации.
  const exhaustedHistory = useRef(new Map<string, number | null>())
  /* --- Отправка ------------------------------------------------------- */

  const send = useCallback(async (input: SendInput): Promise<void> => {
    const text = input.text.trim()
    // У пересылки своего тела нет: пустая подпись — нормальный случай.
    if (!text && !input.media && !input.forwardOf) return

    // §5.2 п. 1: один `client_message_id` на сообщение, создаётся здесь.
    const clientMessageId = uuid()

    await messagesRepo.enqueueLocal({
      conversationId: input.conversationId,
      clientMessageId,
      senderId: input.senderId,
      type:
        input.forwardType ??
        (input.media
          ? input.media.content_type?.startsWith('video')
            ? 'video'
            : input.media.content_type?.startsWith('audio')
              ? 'voice'
              : 'image'
          : 'text'),
      text: text || null,
      replyTo: input.replyTo ?? null,
      media: input.media ?? null,
      forwarded: Boolean(input.forwardOf),
    })

    await queueRepo.enqueue({
      ...baseOp('send_message'),
      kind: 'send_message',
      conversation_id: input.conversationId,
      client_message_id: clientMessageId,
      ...(input.forwardOf ? { forward_of: input.forwardOf } : {}),
      // §10.4: `Idempotency-Key` — один на намерение, переживает повторы.
      idempotency_key: uuid(),
    } as PendingOp)

    await draftsRepo.clear(input.conversationId)
    void runQueue()
  }, [])

  /** C-19 «Повторить»: тот же `client_message_id`, состояние → queued. */
  const retry = useCallback(async (conversationId: string, clientMessageId: string): Promise<void> => {
    await messagesRepo.setLocalStateByClientId(clientMessageId, {
      local_state: 'queued',
      next_attempt_at: null,
      attempt_count: 0,
      error_code: null,
    })

    const existing = (await queueRepo.byConversation(conversationId)).find(
      (op) => op.kind === 'send_message' && op.client_message_id === clientMessageId,
    )

    if (!existing) {
      await queueRepo.enqueue({
        ...baseOp('send_message'),
        kind: 'send_message',
        conversation_id: conversationId,
        client_message_id: clientMessageId,
        idempotency_key: uuid(),
      } as PendingOp)
    } else {
      await queueRepo.recordFailure(existing.op_id, null, 0)
      await queueRepo.release(existing.op_id)
    }

    void runQueue()
  }, [])

  /** C-19 «Удалить черновик»: запись исчезает из истории. */
  const discard = useCallback(async (conversationId: string, clientMessageId: string): Promise<void> => {
    await messagesRepo.dropUnconfirmed(clientMessageId)

    const ops = await queueRepo.byConversation(conversationId)
    for (const op of ops) {
      if (op.kind === 'send_message' && op.client_message_id === clientMessageId) {
        await queueRepo.remove(op.op_id)
      }
    }
  }, [])

  /* --- Редактирование -------------------------------------------------- */

  const edit = useCallback(
    async (conversationId: string, message: { id: string; seq: number }, text: string): Promise<void> => {
      // Оптимистично правим локально: пользователь видит результат сразу.
      const local = await messagesRepo.get(conversationId, message.seq)
      if (local) {
        await messagesRepo.setLocalState(conversationId, message.seq, { text })
      }

      await queueRepo.enqueue({
        ...baseOp('edit_message'),
        kind: 'edit_message',
        conversation_id: conversationId,
        message_id: message.id,
        text,
      } as PendingOp)

      void runQueue()
    },
    [],
  )

  /* --- Удаление --------------------------------------------------------- */

  const remove = useCallback(
    async (conversationId: string, messageId: string, scope: 'me' | 'all'): Promise<void> => {
      await queueRepo.enqueue({
        ...baseOp('delete_message'),
        kind: 'delete_message',
        conversation_id: conversationId,
        message_id: messageId,
        scope,
      } as PendingOp)

      void runQueue()
    },
    [],
  )

  /* --- Реакции ----------------------------------------------------------- */

  const react = useCallback(
    async (
      conversationId: string,
      message: { id: string; seq: number },
      reaction: string | null,
      myUserId: string,
    ): Promise<void> => {
      // Оптимистичное применение: одна реакция на пользователя (BS §4.2).
      const local = await messagesRepo.get(conversationId, message.seq)
      if (local) {
        const reactions = local.reactions.filter((r) => r.user_id !== myUserId)
        if (reaction) reactions.push({ user_id: myUserId, reaction })
        await messagesRepo.setReactions(conversationId, message.seq, reactions)
      }

      await queueRepo.enqueue({
        ...baseOp(reaction ? 'set_reaction' : 'remove_reaction'),
        kind: reaction ? 'set_reaction' : 'remove_reaction',
        conversation_id: conversationId,
        message_id: message.id,
        ...(reaction ? { reaction } : {}),
      } as PendingOp)

      void runQueue()
    },
    [],
  )

  /* --- Квитанции ---------------------------------------------------------- */

  /**
   * CC §5.8: курсоры монотонны. Пробуем WS-кадр `receipt` (дешевле),
   * при отсутствии соединения — очередь и `POST /receipts`.
   */
  const markRead = useCallback(
    async (conversationId: string, seq: number, forceReceipt = false): Promise<void> => {
      const conversation = await conversationsRepo.get(conversationId)
      if (!conversation || (!forceReceipt && conversation.my.last_read_seq >= seq)) return

      if (conversation.my.last_read_seq < seq)
        await conversationsRepo.patchOptimistic(conversationId, {
          my: {
            ...conversation.my,
            last_read_seq: seq,
            last_delivered_seq: Math.max(conversation.my.last_delivered_seq, seq),
          },
          unread_count: 0,
        })

      const sentOverSocket = syncEngine()?.sendReceipt(conversationId, { read_seq: seq, delivered_seq: seq })
      if (sentOverSocket) return

      await queueRepo.mergeReceipt(conversationId, seq, seq)
      void runQueue()
    },
    [],
  )

  /* --- Проекция диалога ----------------------------------------------------- */

  const setMuted = useCallback(async (conversationId: string, mutedUntil: string | null): Promise<void> => {
    await conversationsRepo.patchOptimistic(conversationId, {
      my: { ...(await conversationsRepo.get(conversationId))!.my, muted_until: mutedUntil },
    })

    await queueRepo.enqueue({
      ...baseOp('patch_conversation'),
      kind: 'patch_conversation',
      conversation_id: conversationId,
      patch: { muted_until: mutedUntil },
    } as PendingOp)

    void runQueue()
  }, [])

  const setArchived = useCallback(async (conversationId: string, archived: boolean): Promise<void> => {
    const conversation = await conversationsRepo.get(conversationId)
    if (!conversation) return

    await conversationsRepo.patchOptimistic(conversationId, {
      my: { ...conversation.my, archived_at: archived ? new Date().toISOString() : null },
    })

    await queueRepo.enqueue({
      ...baseOp('patch_conversation'),
      kind: 'patch_conversation',
      conversation_id: conversationId,
      patch: { archived },
    } as PendingOp)

    void runQueue()
  }, [])

  /**
   * CC §4.9 п. 10: очистка истории и скрытие диалога выполняются ТОЛЬКО онлайн.
   * Граница очистки вычисляется сервером в момент выполнения, поэтому
   * отложенный повтор необратимо удалил бы пришедшие за время офлайна сообщения.
   */
  /** Закрепить сообщение (`messageId`) или снять закреплённое (`null`) — факт общий для пары. */
  const pin = useCallback(async (conversationId: string, messageId: string | null): Promise<void> => {
    await queueRepo.enqueue({
      ...baseOp('pin_message'),
      kind: 'pin_message',
      conversation_id: conversationId,
      message_id: messageId,
    } as PendingOp)
    void runQueue()
  }, [])

  const clearHistory = useCallback(async (conversationId: string): Promise<void> => {
    await api.POST('/conversations/{conversationId}/clear', {
      params: { path: { conversationId } },
    })

    const conversation = await conversationsRepo.get(conversationId)
    if (conversation) await messagesRepo.clearBefore(conversationId, conversation.last_seq + 1)
  }, [])

  const hideConversation = useCallback(async (conversationId: string): Promise<void> => {
    await api.DELETE('/conversations/{conversationId}', {
      params: { path: { conversationId } },
    })

    await messagesRepo.clearConversation(conversationId)
    await conversationsRepo.delete(conversationId)
  }, [])

  /* --- Подгрузка истории ------------------------------------------------------ */

  /**
   * Первичная загрузка истории при открытии диалога.
   *
   * Реплика наполняется списками диалогов, но НЕ сообщениями (CC §4.3 шаг 5):
   * история грузится по требованию. Загружаем последнюю страницу, если
   * локально нет сообщений вплоть до `last_seq` — иначе открытый диалог
   * выглядел бы пустым при непустом превью в списке.
   */
  const ensureHistory = useCallback(async (conversationId: string, myUserId: string): Promise<void> => {
    const conversation = await conversationsRepo.get(conversationId)
    if (!conversation) return

    const localMax = await messagesRepo.maxConfirmedSeq(conversationId)
    if (localMax >= conversation.last_seq) return

    const result = unwrap(
      await api.GET('/conversations/{conversationId}/messages', {
        params: { path: { conversationId }, query: { limit: 50 } as never },
      }),
    )

    await messagesRepo.putManyConfirmed(result.items, myUserId, conversation.counterpart_receipts)
  }, [])

  const loadOlder = useCallback(
    async (conversationId: string, myUserId: string, signal?: AbortSignal): Promise<boolean> => {
      const oldest = await messagesRepo.minConfirmedSeq(conversationId)
      if (oldest !== null && oldest <= 1) return false
      if (
        exhaustedHistory.current.has(conversationId) &&
        exhaustedHistory.current.get(conversationId) === oldest
      )
        return false

      const conversation = await conversationsRepo.get(conversationId)

      const result = unwrap(
        await api.GET('/conversations/{conversationId}/messages', {
          params: {
            path: { conversationId },
            query: { limit: 50, ...(oldest ? { before: oldest } : {}) },
          },
          ...(signal ? { signal } : {}),
        }),
      )

      await messagesRepo.putManyConfirmed(result.items, myUserId, conversation?.counterpart_receipts)
      if (!result.has_more)
        exhaustedHistory.current.set(conversationId, await messagesRepo.minConfirmedSeq(conversationId))
      return result.items.length > 0
    },
    [],
  )

  /** Догружаем непрерывную историю до найденного сообщения, сохраняя обычный курсор ленты. */
  const loadThrough = useCallback(
    async (conversationId: string, myUserId: string, seq: number, signal: AbortSignal): Promise<boolean> => {
      for (;;) {
        signal.throwIfAborted()
        if (await messagesRepo.get(conversationId, seq)) return true
        const oldest = await messagesRepo.minConfirmedSeq(conversationId)
        if (oldest !== null && oldest <= seq) return false
        const more = await loadOlder(conversationId, myUserId, signal)
        if (await messagesRepo.get(conversationId, seq)) return true
        const next = await messagesRepo.minConfirmedSeq(conversationId)
        // Очищенная история, удалённая цель или сервер без прогресса не зацикливают переход.
        if (!more || next === null || (oldest !== null && next >= oldest)) return false
      }
    },
    [loadOlder],
  )

  /*
   * Объект действий обязан быть стабильным: экраны держат его в зависимостях
   * эффектов. Без `useMemo` каждая отрисовка давала новый объект, эффект
   * первичной загрузки истории перезапускался бесконечно и сбрасывал признак
   * «история загружена» — пустой диалог так и оставался без «Начните разговор».
   */
  return useMemo(
    () => ({
      send,
      retry,
      discard,
      ensureHistory,
      edit,
      remove,
      react,
      markRead,
      setMuted,
      setArchived,
      pin,
      clearHistory,
      hideConversation,
      loadOlder,
      loadThrough,
    }),
    [
      send,
      retry,
      discard,
      ensureHistory,
      edit,
      remove,
      react,
      markRead,
      setMuted,
      setArchived,
      pin,
      clearHistory,
      hideConversation,
      loadOlder,
      loadThrough,
    ],
  )
}
